//#region Util Imports

import * as _ from 'underscore'
import moment = require('moment')

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'
import { dxSelectBoxOptions } from 'devextreme/ui/select_box'
import { dxDateBoxOptions } from 'devextreme/ui/date_box'
import { dxFormSimpleItem } from 'devextreme/ui/form'
import { PatternRule, RequiredRule } from 'devextreme/ui/validation_engine'

//#endregion

//#region IMR Imports

import { IMRPopupOptions, IIMRPopup } from '@imr/controls.angularjs/components/overlay/popup'
import { IMRComponentInitializedArgs } from '@imr/controls.angularjs/components/base'

//#endregion

import { Enums } from '../../../../../models/common'
import { UserSessionService } from '../../../../../services/user-session'

import { LocalizationHelper, TypeHelper } from '../../../../../helpers'

import { IMRBaseControlController } from '../../../base'

import { CorrespondingActionTypeModel, FieldModel } from '../../../../api/api.models'
import { ImmediatelyArchDateActionOptions, IImmediatelyArchDateAction } from './action-run.dedicated.immediately-get-archive.interfaces'


export class ImmediatelyArchDateActionController extends IMRBaseControlController<IImmediatelyArchDateAction, ImmediatelyArchDateActionOptions> implements ng.IComponentController, IImmediatelyArchDateAction {
    public get LOG_PREFIX(): string {
        return '[SIMAX.Common.ACTION-RUN.DEDICATED.IMMADIATELY_ARCH_DATE_ACTION]'
    }

    public popupOptions: IMRPopupOptions
    public formOptions: DevExpress.ui.dxFormOptions
    public formData: { [key: number]: any }

    private form: DevExpress.ui.dxForm

    private action: CorrespondingActionTypeModel
    private popup: IIMRPopup

    constructor(private readonly userSessionService: UserSessionService) {
        super()
    }

    protected initialize(options: ImmediatelyArchDateActionOptions): JQueryPromise<any> {
        this.setAction(options.action)

        return $.when(this.initializepPopupOptions())
    }

    // #region Popup

    private initializepPopupOptions(): JQueryPromise<any> {
        this.popupOptions = {
            innerOptions: {
                title: this.action.name,
                deferRendering: false,
                onHiding: () => this.onPopupHiding(),
                fullScreen: false,
                maxWidth: 600,
                maxHeight: 300,
            },

            onInitialized: (e: IMRComponentInitializedArgs<IIMRPopup>) => {
                this.initializePopup(e.component)
            },

            bottomToolbarOptions: {
                items: [
                    {
                        location: 'after',
                        widget: 'dxButton',
                        options: {
                            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Send),
                            hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Send),
                            onClick: () => {
                                this.onSubmit()
                            },
                        },
                    },
                    {
                        location: 'after',
                        widget: 'dxButton',
                        options: {
                            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Cancel),
                            hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Cancel),
                            onClick: () => {
                                this.popup.hide()
                            },
                        },
                    },
                ],
            },
        }

        return super.waitForOnInitialized(this.popupOptions)
    }

    // #endregion

    // #region Private Methods

    private onPopupHiding(): void {
        if (_.isFunction(this.options.onHiding)) {
            this.options.onHiding()
        }

        this.clear()
    }

    private clear(): void {
        this.formOptions = null
        this.formData = null
    }

    private initializePopup(component: IIMRPopup): void {
        this.popup = component
    }

    private onSubmit(): void {
        if (_.isFunction(this.options.onActionRunClick)) {
            if (this.form != null && this.form.validate().isValid === true) {
                this.popup.showLoading()
                const actionToSend: CorrespondingActionTypeModel = _.clone(this.action)
                actionToSend.fields = []

                for (let field in this.formData) {
                    const fieldItem: FieldModel = new FieldModel()
                    fieldItem.idDataType = +field
                    fieldItem.index = 0
                    fieldItem.value = this.formData[field]

                    actionToSend.fields.push(fieldItem)
                }

                this.options.onActionRunClick(actionToSend)
                    .always(() => {
                        this.popup.hideLoading()
                        this.popup.hide()
                    })
            }
        }
    }

    // #endregion

    // #region ImmediatelyArchDateAction

    public showPopup(): void {
        this.popup.show()
        this.popup.showLoading()
        this.prepareForm()

        if (this.popup != null) {
            this.popup.getInnerPopup().option('title', this.action.name)
        }
    }

    public setAction(action: CorrespondingActionTypeModel): void {
        this.action = TypeHelper.clone(action, true)
    }

    public hidePopup(): void {
        this.popup.hide()
    }

    // #endregion

    //#region Form

    private prepareForm(): void {
        //FORM

        //#region Form data

        this.setDefaultFormData()

        //#endregion

        this.createForm()
    }

    private createForm(): void {
        this.formOptions = <DevExpress.ui.dxFormOptions>{
            onFieldDataChanged: (e: any) => {
                this.formData[e.dataField] = e.value
            },
            requiredMark: '*',
            showRequiredMark: true,
            labelLocation: 'top',
            colCount: 1,
            width: '100%',
            height: '100%',
            scrollingEnabled: true,
            items: this.getItems(),
            bindingOptions: {
                formData: '$ctrl.formData',
            },
            onInitialized: (e: any) => {
                this.form = e.component
                this.popup.hideLoading()
            },
        }
    }

    private getItems(): dxFormSimpleItem[] {
        return <any[]>[
            {
                dataField: Enums.DataType.DEVICE_TABLE_END_TIME.toString(),
                name: Enums.DataType.DEVICE_TABLE_END_TIME.toString(),
                label: {
                    text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Date),
                },
                validationRules: [this.getValidationPattern(), this.getValidationRequired()],
                editorType: 'dxDateBox',
                editorOptions: this.getDateEditorOptions(),
            },
            {
                dataField: Enums.DataType.DEVICE_TABLE_ROW_COUNT.toString(),
                name: Enums.DataType.DEVICE_TABLE_ROW_COUNT.toString(),
                label: {
                    text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NumberOfDays),
                },
                validationRules: [this.getValidationPattern(), this.getValidationRequired()],
                editorType: 'dxSelectBox',
                editorOptions: this.getDaysCountEditorOptions(),
            },
        ]
    }


    private getValidationPattern(): PatternRule {
        return {
            type: 'pattern',
            pattern: '',
        }
    }

    private getValidationRequired(): RequiredRule {
        return {
            type: 'required',
            message: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.RequiredField),
        }
    }

    private getDaysCountEditorOptions(): dxSelectBoxOptions {
        return <dxSelectBoxOptions>{
            items: [1, 2, 3],
        }
    }
    private getDateEditorOptions(): dxDateBoxOptions {
        return <DevExpress.ui.dxDateBoxOptions>{
            type: 'date',
        }
    }

    private setDefaultFormData(): void {
        this.formData = {}

        this.formData[Enums.DataType.WMBUS_FRAME_SEND_MODE] = 130
        this.formData[Enums.DataType.DEVICE_TABLE_OBJECT_ID] = 1541
        this.formData[Enums.DataType.DEVICE_WMBUS_CUSTOM_DOWNSTREAM_FRAME_PREAMBLE] = 7200
        this.formData[Enums.DataType.DEVICE_WMBUS_CUSTOM_DOWNSTREAM_TRANSMISSION_POWER] = 255
        this.formData[Enums.DataType.DEVICE_WMBUS_CUSTOM_SEND_RESPONSE_IMMEDIATELY] = 1
        this.formData[Enums.DataType.DEVICE_TABLE_END_TIME] = new Date()
        this.formData[Enums.DataType.DEVICE_TABLE_ROW_COUNT] = 1
    }


    //#endregion
}
